<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    :fill="solid ? 'currentColor' : 'none'"
    :stroke="solid ? 'none' : 'currentColor'"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    fill-rule="evenodd"
    clip-rule="evenodd"
    aria-hidden="true"
  >
    <!-- paths = constantes SVG internes (map statique ICONS), jamais une donnée utilisateur. -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <g v-html="paths" />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type IconName =
  | 'calendar' | 'ship' | 'user' | 'users' | 'paw' | 'seat' | 'cabin'
  | 'car' | 'bike' | 'van' | 'moto' | 'walker'
  | 'arrow-left' | 'arrow-right' | 'chevron-right' | 'chevron-left'
  | 'x' | 'check' | 'search' | 'plus' | 'minus' | 'info' | 'alert' | 'swap'
  | 'shower' | 'window' | 'toilet' | 'clock' | 'moon' | 'meal' | 'wifi' | 'star'

const props = withDefaults(defineProps<{ name: IconName; size?: number | string; strokeWidth?: number | string; solid?: boolean }>(), {
  size: 18,
  strokeWidth: 1.75,
  solid: false,
})

// Paths SVG outline 24x24 — design minimaliste line.
const PATHS: Record<IconName, string> = {
  'calendar': '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/>',
  'ship': '<path d="M3 18c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/><path d="M5 14V8a1 1 0 011-1h12a1 1 0 011 1v6"/><path d="M12 4v3M9 11h6"/>',
  'user': '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0116 0"/>',
  'users': '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20a6.5 6.5 0 0113 0"/><circle cx="17" cy="9" r="2.5"/><path d="M16 14a5 5 0 015.5 5"/>',
  'paw': '<circle cx="6" cy="10" r="2"/><circle cx="10" cy="6" r="2"/><circle cx="14" cy="6" r="2"/><circle cx="18" cy="10" r="2"/><path d="M8 14a4 4 0 008 0c0-3-2-4-4-4s-4 1-4 4z"/>',
  'seat': '<path d="M6 21V11a2 2 0 012-2h2v12"/><path d="M14 21v-3a3 3 0 013-3h1v6"/><path d="M10 13h4"/>',
  'cabin': '<rect x="3" y="11" width="18" height="9" rx="1"/><path d="M3 14h18M7 11V8a5 5 0 0110 0v3"/>',
  'car': '<path d="M5 17V11l2-5h10l2 5v6"/><path d="M3 17h18M3 20h2v-3M19 20h2v-3"/><circle cx="7.5" cy="17" r="1.5"/><circle cx="16.5" cy="17" r="1.5"/>',
  'bike': '<circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17l4-7h4l4 7M10 10l-2-3h3M14 10l3 3"/>',
  'van': '<path d="M3 17V8a1 1 0 011-1h11l4 4v6"/><path d="M3 17h18M3 20h2v-3M19 20h2v-3M15 7v4h4"/><circle cx="7" cy="17" r="1.5"/><circle cx="17" cy="17" r="1.5"/>',
  'moto': '<circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M5 17l5-8h4l3 5M14 9h4M10 9l-2-2"/>',
  'walker': '<circle cx="12" cy="4" r="2"/><path d="M10 8h4l2 6-3 1v6M11 14l-3 3v4"/>',
  'arrow-left': '<path d="M19 12H5m0 0l7 7m-7-7l7-7"/>',
  'arrow-right': '<path d="M5 12h14m0 0l-7-7m7 7l-7 7"/>',
  'chevron-right': '<path d="M9 6l6 6-6 6"/>',
  'chevron-left': '<path d="M15 6l-6 6 6 6"/>',
  'x': '<path d="M18 6L6 18M6 6l12 12"/>',
  'check': '<path d="M5 13l4 4L19 7"/>',
  'search': '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
  'swap': '<path d="M7 4v16m0 0l-3.5-3.5M7 20l3.5-3.5M17 20V4m0 0l-3.5 3.5M17 4l3.5 3.5"/>',
  'plus': '<path d="M12 5v14M5 12h14"/>',
  'minus': '<path d="M5 12h14"/>',
  'info': '<circle cx="12" cy="12" r="9"/><path d="M12 8v.01M11 12h1v5h1"/>',
  'alert': '<path d="M12 3l10 18H2L12 3z"/><path d="M12 10v5M12 18v.01"/>',
  'shower': '<path d="M6 20V8a3 3 0 016 0v2"/><path d="M3 13h18l-1 2H4l-1-2zM7 18v2M12 18v2M17 18v2"/>',
  'window': '<rect x="4" y="4" width="16" height="16" rx="1"/><path d="M12 4v16M4 12h16"/>',
  'toilet': '<path d="M6 4h12v6a6 6 0 01-12 0V4z"/><path d="M9 16l-1 4h8l-1-4"/>',
  'clock': '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  'moon': '<path d="M20 14.5A8 8 0 119.5 4a7 7 0 0010.5 10.5z"/>',
  'wifi': '<path d="M5 12.5a10 10 0 0114 0"/><path d="M8.5 15.5a5 5 0 017 0"/><path d="M2 9a15 15 0 0120 0"/><circle cx="12" cy="19" r="1"/>',
  'star': '<path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z"/>',
  'meal': '<path d="M4 9h13v2a6 6 0 01-6 6h-1a6 6 0 01-6-6V9z"/><path d="M17 11h2a2 2 0 010 4h-2"/><path d="M7 6c0-1 1-1 1-2M11 6c0-1 1-1 1-2M14 6c0-1 1-1 1-2"/>',
}

// Variantes pleines (silhouettes) pour les pictos d'aménités de la ligne
// traversée — alignées sur les icônes solides des comparateurs (cf. réf design).
// fill-rule evenodd : les sous-tracés internes (roues, vitre) creusent des trous.
const SOLID_PATHS: Partial<Record<IconName, string>> = {
  'car': '<path d="M3 16.4V13a2 2 0 0 1 1.45-1.92L6 6.7A2.2 2.2 0 0 1 8.1 5.2h7.8A2.2 2.2 0 0 1 18 6.7l1.55 4.38A2 2 0 0 1 21 13v3.4a.8.8 0 0 1-.8.8H19.2a2.1 2.1 0 0 1-4.2 0H9a2.1 2.1 0 0 1-4.2 0H3.8a.8.8 0 0 1-.8-.8Zm5-9.2L6.9 10.7h10.2L16 7.2a.7.7 0 0 0-.66-.5H8.66a.7.7 0 0 0-.66.5ZM7 14.1a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Zm10 0a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"/>',
  'cabin': '<path d="M3 8.4a1 1 0 0 1 2 0V12h5.2v-1.4A1.6 1.6 0 0 1 11.8 9H18a3 3 0 0 1 3 3v6.1a1 1 0 0 1-2 0V16.8H5v1.3a1 1 0 0 1-2 0V8.4Z"/>',
  'paw': '<path d="M6.4 9.2a1.95 1.95 0 1 1 0 3.9 1.95 1.95 0 0 1 0-3.9Zm11.2 0a1.95 1.95 0 1 1 0 3.9 1.95 1.95 0 0 1 0-3.9ZM10 5.6a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm4 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm-2 6.6c2.55 0 4.5 1.85 4.5 4.05 0 1.7-1.4 2.55-3 2.55-.85 0-1.1-.3-1.5-.3s-.65.3-1.5.3c-1.6 0-3-.85-3-2.55 0-2.2 1.95-4.05 4.5-4.05Z"/>',
  'seat': '<path d="M5.5 4.2a1.4 1.4 0 0 1 1.4 1.45L6.6 12.6a2 2 0 0 0 2 2.1H14v2.1H8.4a4 4 0 0 1-4-3.8l-.3-7.3A1.4 1.4 0 0 1 5.5 4.2Zm10.7 8.4a2.6 2.6 0 0 1 2.6 2.6V19a1 1 0 0 1-2 0v-3.8a.6.6 0 0 0-.6-.6H10.5a1 1 0 0 1 0-2h5.7Z"/>',
}

const paths = computed(() => (props.solid ? SOLID_PATHS[props.name] : undefined) ?? PATHS[props.name] ?? '')
</script>
